import { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useBreadcrumbStore } from '@/stores/breadcrumb.store';

const routeLabels: Record<string, string> = {
  'partner-portal': 'Partner Home', 'partner-commission': 'Partner Commission', 'commission-report': 'Commission Report',
  dashboard: 'Dashboard',
  subscribers: 'Subscribers',
  billing: 'Billing',
  invoices: 'Invoices',
  plans: 'Plans',
  tickets: 'Tickets',
  noc: 'NOC',
  olt: 'OLT / GPON',
  nas: 'NAS / Routers',
  reports: 'Reports',
  settings: 'Settings',
  logs: 'Logs',
  general: 'General',
  users: 'Users',
  roles: 'Roles',
  'api-management': 'API Management',
  'service-mapping': 'Service Mapping',
  credentials: 'Credentials',
  // Field Ops
  dispatch: 'Dispatch',
  command: 'Command Center',
  map: 'Map',
  history: 'Technician History',
  employees: 'Employees',
  technicians: 'Technicians',
  outages: 'Outage Console',
  expenses: 'Expense Approvals',
  inventory: 'Field Inventory',
  geofences: 'Geofences',
  jobs: 'Jobs',
};

// A segment can mean different things under different parents: `inventory` under /dispatch is the technician
// field inventory, at the root it is the Inventory module. Full-path labels win over segment labels.
const pathLabels: Record<string, string> = {
  '/inventory': 'Inventory',
  '/inventory/stock/drums': 'Fibre Drums',
  '/inventory/stock/transfers': 'Transfers',
  '/inventory/stock/partners': 'Partner Stock',
  '/inventory/stock/audits': 'Stock Audit',
  '/inventory/reports': 'Reports',
  '/inventory/reports/current': 'Current Stock', '/inventory/reports/movement': 'Stock Movement', '/inventory/reports/valuation': 'Stock Valuation',
  '/inventory/reports/transfers': 'Stock Transfers', '/inventory/reports/grn': 'Purchase / GRN', '/inventory/reports/issue': 'Job Material Issue',
  '/inventory/reports/consumption': 'Job Material Consumption', '/inventory/reports/custody': 'Technician Custody', '/inventory/reports/returns': 'Pending Returns',
  '/inventory/reports/subscriber_assets': 'Subscriber Assets', '/inventory/reports/partner': 'Partner Inventory', '/inventory/reports/pop_assets': 'POP Assets',
  '/inventory/reports/asset_history': 'Asset History', '/inventory/reports/drum_balance': 'Fibre Drum Balance', '/inventory/reports/fibre_monthly': 'Fibre Consumption',
  '/inventory/reports/cable_job': 'Cable by Job', '/inventory/reports/cable_tech': 'Cable by Technician', '/inventory/reports/cable_partner': 'Cable by Partner',
  '/inventory/reports/variance': 'Cable Variance', '/inventory/reports/rma': 'RMA / Repair', '/inventory/reports/damaged': 'Damaged / Scrap', '/inventory/reports/audit': 'Inventory Audit',
  '/inventory/alerts': 'Alerts',
  '/inventory/jobs': 'Job Materials',
  '/inventory/jobs/issue': 'Issue',
  '/inventory/jobs/returns': 'Returns',
  '/inventory/jobs/variances': 'Variances',
  '/inventory/jobs/custody': 'Technician Custody',
  '/inventory/purchasing': 'Purchasing',
  '/inventory/purchasing/orders': 'Purchase Orders',
  '/inventory/purchasing/grns': 'GRNs',
  '/inventory/purchasing/import': 'Bulk Serial Import',
  '/inventory/assets': 'Assets',
  '/inventory/assets/subscribers': 'Subscriber Assets',
  '/inventory/assets/pops': 'POP / Network',
  '/inventory/assets/testing': 'Testing & RMA',
  '/inventory/assets/damaged': 'Damaged / Scrap',
  '/settings/inventory': 'Inventory Setup',
  '/settings/inventory/items': 'Item Master',
  '/settings/inventory/vendors': 'Vendors',
  '/settings/inventory/alerts': 'Alerts',
  '/settings/inventory/opening-stock': 'Opening Stock',
};

// UUID pattern to detect dynamic ID segments
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}/i;
const isIdSegment = (s: string) => UUID_RE.test(s) || /^\d+$/.test(s);

interface BreadcrumbProps {
  className?: string;
}

export const Breadcrumb = memo(function Breadcrumb({ className }: BreadcrumbProps) {
  const location = useLocation();
  const entityLabels = useBreadcrumbStore((s) => s.labels);
  const segments = location.pathname.split('/').filter(Boolean);

  const crumbs = useMemo(() => segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    let label: string;

    if (isIdSegment(segment)) {
      // Use entity name from store if available, otherwise truncated ID
      label = entityLabels[segment] ?? segment.slice(0, 8) + '...';
    } else {
      label = pathLabels[path] ?? routeLabels[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    const isLast = index === segments.length - 1;
    return { path, label, isLast };
  }), [location.pathname, entityLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  if (segments.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center gap-1.5 text-sm', className)}>
      <Link
        to="/dashboard"
        className="text-muted-foreground transition-colors hover:text-foreground"
      >
        <Home className="h-3.5 w-3.5" />
      </Link>

      {crumbs.map((crumb) => (
        <span key={crumb.path} className="flex items-center gap-1.5">
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
          {crumb.isLast ? (
            <span className="max-w-[160px] truncate font-medium text-foreground">{crumb.label}</span>
          ) : (
            <Link
              to={crumb.path}
              className="max-w-[160px] truncate text-muted-foreground transition-colors hover:text-foreground"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
});
